import React, {useState, useMemo, useCallback, useRef, Suspense } from "react";
import CreateAccountModal from "./create-acct-modal";
import Loading from "../swt-loading";
import AdminTableView from "./AdminTableView";
import IntegrationsDropdown from './IntegrationsDropdown';
import Search from "./search";
import * as S from '../../../../styles/core-styles/AdminTools-styles';
import { formatDateTimeUTC, formatEmail, trimData } from "../UtilityFunctions";
import { _inputSort, _stringSort, _dropdownSort, rowsToData, columnsToExcelHeaders } from "./TableHelpers";
import { InputValidators } from "../../utility-components/ValidationPatterns";
import { processApiResponse } from "../../utility-components/ConformUnits";

//code-splitting imports
const ExcelDownloadButton = React.lazy(() => import("./ExcelDownloadButton"));

const SERVER_ERRORED_MESSAGE = "There was an error with your request. Please try again"

export default function SuperAdminDatabaseSettings(props){

  const integrationsObj = require("../integrations");
  const integrations = integrationsObj.integrations;
  const [showModal, setShowModal] = useState( false);
  const [databases, setDatabases] = useState(null);
  const [updateDatabases, setUpdateDatabases] = useState(true);
  const [products, setProducts] = useState(null)
  const [matchingDatabases, setMatchingDatabases] = useState(null);
  
  const skipPageResetRef = useRef(false);

  useMemo(() => {
    if(!updateDatabases)return;
    //if (!databases || !products)resetState();
    try {
      let userEmail = formatEmail(props.user.email)
      const url = `${props.apiURL}getDatabaseList?email=${userEmail}`;
      fetch(url, {
        credentials: "same-origin",
        headers: { Authorization: `Bearer ${props.user.token}` },
      })
      .then(resp => resp.json())
      .then(data => {
        if (data.status === "error")
          alert(SERVER_ERRORED_MESSAGE);
        else {
          data.data.forEach((db) => {
            //Keep track of original product list to reference which products have been added/deleted
            db['original_product_list'] = JSON.parse(JSON.stringify(db.products))
            db.last_login_original = JSON.parse(JSON.stringify(db.last_login))
            db.last_login = formatDateTimeUTC(db.last_login);
            db = processApiResponse(props.user.userSettings, db, false)
          })
          setDatabases(data.data);
          setUpdateDatabases(false);
        }
      }
      )
    } catch (err) {
      console.error(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[updateDatabases]);

  useMemo(() => {
    let url = props.apiURL + "getProductList";
    fetch(url, {
      headers: { Authorization: `Bearer ${props.user.token}` },
    })
      .then(props.handleFetchErrors)
      .then((res) => res.json())
      .then((data) => {
        if (data.data) {
          const products = data.data.filter((p) => p.deprecated !== true);
          setProducts(products);
        } else throw Error("No product list available");
      })
      .then(() => {
        return true;
      })
      .catch((err) => console.error("error on product list call", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);


  const boolSort = useMemo(() => (rowA, rowB, columnId) => {
    const a = rowA.values[columnId].props.bool;
    const b = rowB.values[columnId].props.bool;
    return a > b ? 1 : -1;
  },[]);

  const dateSort = useMemo(() => (rowA, rowB) => {
    const a = rowA.original.last_login_original;
    const b = rowB.original.last_login_original;
    if(a && !b) {
      return 1;
    } else if(!a && b) {
      return -1;
    } else {
    return a > b ? 1 : -1;
    }
  },[])

  const tableColumns = [
    {Header: "Database", accessor: "database", sortType: _stringSort, showInDownload: true},
    {Header: "Display Name", accessor: "display_name", sortType: _inputSort, showInDownload: true},
    {Header: 'Total Users', accessor: 'all_user_count', width: 120, showInDownload: true},
    {Header: 'Non Super Admin Users', accessor: 'non_super_admin_user_count', width: 120, showInDownload: true},
    {Header: 'User Role Count\n(S/P/F/B)', accessor: 'user_role_counts', width: 200, showInDownload: true, explainText: 'This is the number of users assigned to each user role in the database. Each user is counted once based on their highest role.\nS = Super Admin\nP = Partner Admin\nF = Fleet Admin\nB = Basic User'},
    {Header: 'Last Login', accessor: 'last_login', sortType: dateSort, showInDownload: true, explainText: 'Last login is user specific and does not include super admin user logins. This is the most recent login for any user that is a member of the database.'},
    {Header: "Integration", accessor: "integration", sortType: _dropdownSort, showInDownload: true},
    {Header: "ezEV (TD)", accessor: "ezev-td", sortType: boolSort, width: 120, showInDownload: true},
    {Header: "ezEV (FX)", accessor: "ezev-fx", sortType: boolSort, width: 120, showInDownload: true},
    {Header: "ionEV", accessor: "ionev", sortType: boolSort, width: 120, showInDownload: true},
    {Header: "Emit", accessor: "emit", sortType: boolSort, width: 120, showInDownload: true},
    {Header: "ezIO", accessor: "evse_map", sortType: boolSort, width: 120, showInDownload: true},
    {Header: "Shifted", accessor: "shifted", sortType: boolSort, width: 120, showInDownload: true},
    {Header: "Admin", accessor: "admin", sortType: boolSort, width: 120, showInDownload: true},
    {Header: "Dev Tools", accessor: "dev_tools", sortType: boolSort, width: 120, showInDownload: true},
    {Header: 'Location Edit Tool', accessor: 'location_edit_tool', sortType: boolSort, width: 150, showInDownload: true}
  ]

  function createDatabase(data){
    const url = `${props.apiURL}setupDb`;
    data.dbName = data.dbName.toLowerCase();
    data.display_name = data.companyName;
    data = trimData(data);
    try {
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${props.user.token}`,
        },
        body: JSON.stringify(data),
      })
        .then((response) => response.json())
        .then((data) => {
          addUserToDb(data.dbName);
        });
    } catch (err) {
      console.error("error", err);
    }
  };

  function addUserToDb(dbName){
    const url = `${props.apiURL}updateUserDatabases`;
    // NOTE want account to be specified in setup modal?
    var data = {
      email: props.user.email,
      database: dbName,
      account: dbName,
    };
    try {
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${props.user.token}`,
        },
        body: JSON.stringify(data),
      })
      .then((resp) => {return resp.json()})
      .then((data) => {
        if (data.status === "error")
          alert("There was a server error during your request.");
        else 
          alert('Updated users databases.')
      });
    } catch (err) {
      console.error("error", err);
    }
  };


function handleProductClick(id, product) {
  skipPageResetRef.current = false;
  let dbs = [...databases];
  let row = _getTableIndex(id);
  let db = dbs[row]
  db['mutated'] = true
  if(db.products.includes(product)) {
    db['products'] = db.products.filter((p) => p !== product);
  } else {
    db['products'] = [...db.products, product]
  }
  setDatabases(dbs)
};

  function createAcct (data){
    createDatabase(data);
    setShowModal(false)
  };

  function createDatabaseOptions () {
    if (integrations === undefined || integrations === null) return;
    return integrations.map((opt) => {
      if (opt.available)
        return (
          <option key={opt.id} id={opt.id} value={opt.id}>
            {opt.name}
          </option>
        );
      return null;
    });
  };

  function handleIntegrationChange(e, id) {
    skipPageResetRef.current=false;
    let dbs = [...databases];
    let row = _getTableIndex(id);
    let db = dbs[row];
    db['mutated'] = true;
    db['integration'] = e;
    setDatabases(dbs);
  };

  function _getTableIndex(db) {
    const idxArr = databases.map((d) => {
      return d.database;
    });
    return idxArr.indexOf(db);
  }

  function handleInputChange(e){
    const inputValidationRegex = e.target.getAttribute('accessor') === 'display_name' ? new RegExp(InputValidators.latinParen.pattern, 'v') : new RegExp(InputValidators.basicParen.pattern);
    if(inputValidationRegex.test(e.target.value) === false) {
      e.preventDefault();
      return;
    }
    skipPageResetRef.current=false;
    const dbs = [...databases];
    const dbObj = dbs[_getTableIndex(e.target.getAttribute("id"))];
    dbObj["mutated"] = true;
    let val = e.target.value.normalize("NFC");
    val = val.replace(/['"]/g, "");
    dbObj[e.target.getAttribute("accessor")] = val;
    setDatabases(dbs);
  }

  const InputTableCell = useCallback((props) => 
      <input className="swt-admin-input"
             style={props.styles}
             id={props.id}
             title={props.label}
             key={props.id}
             value={`${props.label}`}
             accessor={props.accessor}
             onChange={props.handleOnChange}
             type={props.type}
             onMouseOver={props.onMouseOver ? ()=>props.onMouseOver(props.vin) : ()=>{}}
             onMouseOut={props.onMouseOver ? ()=>props.onMouseOut(props.vin) : ()=>{}}
      />,[]);

  const BoolTableCell = useCallback((props) =>
        <input
            className="swt-admin-table-input"
            type="checkbox"
            id={props.id}
            key={props.id}
            accessor={props.accessor} 
            style={{color: props.bool ? "black" : "transparent"}} 
            onChange={props.handleOnClick}
            checked={(props.bool === null || typeof props.bool === "undefined") ? false : props.bool}
            disabled={(typeof props.bool === "undefined" || props.disabled) ? true : false}
        />,[]);


  function sanitizeIntegrationId(integration, options) {
    let availableIntegrations = options.filter((opt) => opt.available === true)
    if (availableIntegrations.find((i) => i.id === integration)) {
      return integration;
    } else {
      return 'none'
    }
  }

  const mappedDatabases = useMemo(() => {
    if(!databases || !matchingDatabases) return null;
    if(databases.length < 1)return [];
    const immutableDatabases = JSON.parse(JSON.stringify(databases));
    const matchingDbs = matchingDatabases.map((d) => {return d.database});
    return immutableDatabases.filter((acc) => {
        if(matchingDbs.indexOf(acc.database) < 0)return null;
        acc.user_role_counts = `${acc.super_admin_user_count}/${acc.partner_admin_user_count}/${acc.fleet_admin_user_count}/${acc.basic_user_count}`;
        acc.last_login = acc.last_login ? `${acc.last_login.date} - ${acc.last_login.time}` : null;
        acc.display_name = <InputTableCell id={acc.database} label={acc.display_name} accessor={"display_name"} handleOnChange={handleInputChange}type={"text"}/>
        acc['ezev-td'] = <BoolTableCell id={acc.database} accessor={"ezev-td"} bool={acc.products.includes("ezev-td")} handleOnClick={() => {handleProductClick(acc.database, 'ezev-td')}} />
        acc['ezev-fx'] = <BoolTableCell id={acc.database} accessor={"ezev-fx"} bool={acc.products.includes("ezev-fx")} handleOnClick={() => {handleProductClick(acc.database, 'ezev-fx')}} />
        acc.ionev = <BoolTableCell id={acc.database} accessor={"ionev"} bool={acc.products.includes("ionev")} handleOnClick={() => {handleProductClick(acc.database, 'ionev')}} />
        acc.emit = <BoolTableCell id={acc.database} accessor={"emit"} bool={acc.products.includes("emit")} handleOnClick={() => {handleProductClick(acc.database, 'emit')}} />
        acc.shifted = <BoolTableCell id={acc.database} accessor={"shifted"} bool={acc.products.includes("shifted")} handleOnClick={() => {handleProductClick(acc.database, 'shifted')}} disabled={true}/>
        acc.evse_map = <BoolTableCell id={acc.database} accessor={"evse_map"} bool={acc.products.includes("evse-map")} handleOnClick={() => {handleProductClick(acc.database, 'evse-map')}} />
        acc.admin = <BoolTableCell id={acc.database} accessor={"admin"} bool={acc.products.includes("admin")} handleOnClick={() => {handleProductClick(acc.database, 'admin')}} />
        acc.dev_tools = <BoolTableCell id={acc.database} accessor={"dev_tools"} bool={acc.products.includes("dev-tools")} handleOnClick={() => {handleProductClick(acc.database, 'dev-tools')}} />
        acc.location_edit_tool = <BoolTableCell id={acc.database} accessor={'location_edit_tool'} bool={acc.products.includes('location-edit-tool')} handleOnClick={() => {handleProductClick(acc.database, 'location-edit-tool')}}/>
        
        acc.integration = <IntegrationsDropdown
          defaultValue={sanitizeIntegrationId(acc.integration, integrations)}
          integrations={integrations}
          handleChange={handleIntegrationChange}
          id={acc.database}
        />
        return acc;
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[databases, matchingDatabases])

  const handleSave = () => {
    let data = databases.filter((db) => db.mutated === true);
    if(data.length === 0 ) {
      alert('There are no databases that have been changed.');
      return;
    } else {
      let productsAdded = false;
      data.forEach((db) => {
        //Check the original product list to see what products have been added/deleted
        db['products_added'] = db.products.filter((d) => !db.original_product_list.includes(d));
        if(db['products_added'].length > 0) productsAdded = true;
        db['products_deleted'] = db.original_product_list.filter((d) => !db.products.includes(d))
        // Reset the list
        db.original_product_list = db.products
        db = trimData(db)
      })
      let successAlertMessage = "Updates successful.";
      if (productsAdded) successAlertMessage += " Note that added products will need to be manually enabled for each user in the database's settings.";
      if (data.findIndex((db) => db.database === props.activeDb) !== -1) successAlertMessage += "\nYou may need to refresh the page for changes to take effect.";
      let url = `${props.apiURL}updateDatabases/`;
      fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${props.user.token}`,
        },
        body: JSON.stringify(data),
      })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "error") {
          alert(SERVER_ERRORED_MESSAGE);
        } else {
          alert(successAlertMessage);
        }
      })
    }
  };

  const formattedExcelData = useMemo(() => {
    if(!databases) return [];
    //Dont want to alter original database list
    const copiedDbList = JSON.parse(JSON.stringify(databases))
    return copiedDbList.map((acc) => {
        acc.user_role_counts = `${acc.super_admin_user_count}/${acc.partner_admin_user_count}/${acc.fleet_admin_user_count}/${acc.basic_user_count}`;
        acc.last_login = acc.last_login ? `${acc.last_login.date} - ${acc.last_login.time}` : null;
        acc['ezev-td'] = acc.products.includes("ezev-td")
        acc['ezev-fx'] = acc.products.includes("ezev-fx")
        acc.ionev = acc.products.includes("ionev")
        acc.emit = acc.products.includes("emit")
        acc.shifted = acc.products.includes("shifted")
        acc.evse_map = acc.products.includes("evse-map")
        acc.admin = acc.products.includes("admin")
        acc.dev_tools = acc.products.includes("dev-tools")
        acc.location_edit_tool = acc.products.includes('location-edit-tool')
        return acc
    })

  },[databases]) 

    if (!databases || !products)
      return <Loading />;
    return (
      <>
          <S.CDRTableHeaderContainer justifyContent={'space-between'}>
              <div>
                  <S.AdminTableExplainTextPrimary>List of all databases.</S.AdminTableExplainTextPrimary>
                  <S.AdminTableExplainTextSub>Click a column name to sort the table.</S.AdminTableExplainTextSub> 
                  <S.SearchContainer>
                    <Search allValues={databases} setMatchingValues={setMatchingDatabases} skipPageResetRef={skipPageResetRef} disabled={databases ? false : true} accentInsensitive={true}/>
                  </S.SearchContainer>
                  <S.CtaButtonWrapper>
                    <S.CtaButton onClick={() => setShowModal(!showModal)}>Add</S.CtaButton>
                    <S.CtaButton onClick={handleSave}>Update</S.CtaButton>
                  </S.CtaButtonWrapper>
              </div>

               <Suspense fallback={<div></div>}>
                  <ExcelDownloadButton
                    downloadType={"cdrDbSettingsDownload"}
                    columns={columnsToExcelHeaders(tableColumns)}
                    data={rowsToData(tableColumns, formattedExcelData, ((a,b) => a.database?.toLowerCase() > b.database?.toLowerCase() ? 1 : -1))}
                    userSettings={props.user.userSettings}
                  />
                </Suspense>
          </S.CDRTableHeaderContainer>
          {!mappedDatabases && <Loading />}
          {(mappedDatabases) && 
            <AdminTableView 
              columns={tableColumns}
              data={mappedDatabases}
              stickyCols={2}
              skipPageResetRef={skipPageResetRef}
            />
          }

          <CreateAccountModal
            show={showModal}
            user={props.user}
            apiURL={props.apiURL}
            handleClose={() => setShowModal(!showModal)}
            integrations={integrations}
            credentials={props.credentials}
            createAcct={createAcct}
            createDatabaseOptions={createDatabaseOptions}
            passwordRules={props.passwordRules}
          />
      </>
    );
  }


